import { PauseCircle, RefreshCw } from "lucide-react";
import SiteLogo from "@/components/common/SiteLogo";
import { Button } from "@/components/ui/button";
import type { EnvStatusEntry } from "@/config/env-status";

/** A human-readable "expected back" line from the schedule-derived next-on time. */
function formatNextOn(entry: EnvStatusEntry): string | null {
  if (!entry.nextOnAt) return null;
  const when = new Date(entry.nextOnAt);
  if (Number.isNaN(when.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: entry.timezone || undefined,
      timeZoneName: "short",
    }).format(when);
  } catch {
    return when.toLocaleString();
  }
}

/**
 * Full-screen "this environment is paused" notice, shown when the EMS backend this build
 * targets has been powered down on a schedule. Theme-aware (uses the app's design tokens).
 */
export default function EnvironmentPausedScreen({ entry }: { entry: EnvStatusEntry }) {
  const starting = entry.status === "starting";
  const nextOn = formatNextOn(entry);

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
      <div className="text-center space-y-6 max-w-md mx-auto">
        <SiteLogo className="mx-auto h-9 w-36" />
        <div className="mx-auto w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
          <PauseCircle className="h-10 w-10 text-muted-foreground" />
        </div>
        <div className="space-y-3">
          <h1 className="text-2xl font-bold tracking-tight">
            {starting ? `${entry.label} is starting up` : `${entry.label} is paused`}
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {starting
              ? "This environment is powering back on. Give it a minute, then retry."
              : "This non-production environment is powered down on a schedule to save resources. It comes back automatically — no action needed."}
          </p>
          {nextOn && !starting && (
            <p className="text-sm text-foreground font-medium">Expected back: {nextOn}</p>
          )}
        </div>
        <div className="flex justify-center pt-2">
          <Button size="lg" variant="outline" onClick={() => window.location.reload()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    </div>
  );
}
